import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Header from '../../components/Header';
import { fetchData, postData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/numberUtils';

interface ImportResult {
  dryRun: boolean;
  rowsTotal: number;
  rowsNew: number;
  minOrderedAt: string | null;
  maxOrderedAt: string | null;
  matchedSure: number;
  matchedUnsure: number;
  unmatched: number;
  bannersFilled: number;
  bannersFilledWebinar: number;
  bannersRevoked: number;
  filledSamples: Array<{ ordernumber: string; banner: string; createdOn: string }>;
  stubsFilled?: number;
  stubsRevoked?: number;
  stubsSkipped?: Record<string, number>;
  stubSamples?: Array<{ ordernumber: string; artikel: string; wkz: string; createdOn: string }>;
  paymentMethods: Record<string, number>;
}

interface ImportStatus {
  tableReady: boolean;
  lastImports: Array<{
    id: number;
    imported_at: string;
    imported_by: string | null;
    rows_total: number;
    rows_new: number;
    matched_sure: number;
    matched_unsure: number;
    unmatched: number;
    banners_filled: number;
    max_ordered_at: string | null;
  }>;
  totals: { rows: number; matchedSure: number; bannersFilled: number; maxOrderedAt: string | null };
  paymentMethods: Array<{ zahlungsmethode: string; n: number }>;
}

// Gruende aus decideStubFills (helpers/pxDataOrderTicker.ts), fuer Menschen lesbar.
const STUB_SKIP_LABELS: Record<string, string> = {
  ohne_banner: 'ohne Banner',
  topf_ungleich: 'Bestellzahl am Tag weicht ab',
  wkz_abweichung: 'WKZ weicht bei vollständigen Bestellungen ab',
  px_ohne_wkz: 'PX ohne WKZ',
  wkz_ohne_config: 'WKZ ohne Konfiguration',
  mit_kunde: 'PL-X hat schon einen Kunden angelegt',
};

const STEPS = [
  'In PX-Data (QuickSight) das Dashboard „Order-Ticker“ öffnen, Filter auf „All“ lassen.',
  'Über der Tabelle „Bestelleingang“ das Menü (⋮) → „Export to CSV“.',
  'Oben rechts „Export“ → „View exports“ → die Datei herunterladen.',
  'Hier auswählen, „Prüfen“ klicken, Ergebnis ansehen, dann „Importieren“.',
];

const PxImportPage: FC = () => {
  const [status, setStatus] = useState<ImportStatus | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<ImportResult | null>(null);
  const [imported, setImported] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    const r = await fetchData<ImportStatus>('/api/plx-px-import/status');
    if (r.success && r.data) setStatus(r.data);
    else setError(r.error || 'Status konnte nicht geladen werden');
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const csv = await file.text();
      const r = await postData<ImportResult>('/api/plx-px-import/order-ticker', { csv, dryRun });
      if (!r.success || !r.data) {
        setError(r.error || 'Import fehlgeschlagen');
      } else if (dryRun) {
        setChecked(r.data);
        setImported(null);
      } else {
        setImported(r.data);
        setChecked(null);
        await loadStatus();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Datei konnte nicht gelesen werden');
    }
    setBusy(false);
  };

  const result = imported ?? checked;

  return (
    <Box m="20px">
      <Header
        title="PX-Data Import"
        subtitle="Order-Ticker aus QuickSight: Zahlart je Bestellung + fehlende Banner ergänzen"
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Der Import läuft täglich um 14:40 Uhr von selbst. Diese Seite brauchst du nur, wenn du
        einen Export selbst prüfen willst oder der automatische Lauf eine Datei abgelehnt hat –
        ein Import von Hand setzt dann den neuen Vergleichsstand.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 1 }}>
            So geht’s von Hand
          </Typography>
          <ol style={{ margin: 0, paddingLeft: 20, listStyleType: 'decimal' }}>
            {STEPS.map((s) => (
              <li key={s}>
                <Typography variant="body2">{s}</Typography>
              </li>
            ))}
          </ol>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Bestehende Banner werden nie überschrieben. Aufgefüllt wird nur, wo unser Banner leer ist,
            die Bestelladresse keine Kampagnenmarke (affiliate) trägt und die Zuordnung eindeutig
            ist; die Webinar-Zuordnung übernimmt das beim nächsten stündlichen Lauf. Eine frühere
            Ergänzung, die ein neuer Export nicht mehr stützt, wird zurückgenommen.
          </Typography>
        </CardContent>
      </Card>

      {status && !status.tableReady && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Die Tabellen für den Import fehlen noch (Migration 20260930_plx_px_orders.sql). „Prüfen“
          funktioniert trotzdem, „Importieren“ erst danach.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" component="label" disabled={busy}>
            CSV auswählen
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setChecked(null);
                setImported(null);
                setError(null);
              }}
            />
          </Button>
          <Typography variant="body2">{file ? file.name : 'keine Datei gewählt'}</Typography>
          <Button variant="contained" color="secondary" disabled={!file || busy} onClick={() => run(true)}>
            Prüfen
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!checked || busy || (status !== null && !status.tableReady)}
            onClick={() => run(false)}
          >
            Importieren
          </Button>
          {busy && <Typography variant="body2">läuft …</Typography>}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {result.dryRun ? 'Prüfung (noch nichts geschrieben)' : 'Import abgeschlossen'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`${formatNumber(result.rowsTotal, 0)} Bestellungen`} />
              <Chip label={`${formatNumber(result.rowsNew, 0)} neu`} />
              <Chip label={`Zeitraum ${result.minOrderedAt?.slice(0, 10)} – ${result.maxOrderedAt?.slice(0, 16)}`} />
              <Chip color="success" label={`${formatNumber(result.matchedSure, 0)} sicher zugeordnet`} />
              <Chip label={`${formatNumber(result.matchedUnsure, 0)} unsicher`} />
              <Chip label={`${formatNumber(result.unmatched, 0)} ohne Treffer`} />
              <Chip
                color="primary"
                label={
                  result.dryRun
                    ? `${formatNumber(result.bannersFilled, 0)} Banner würden ergänzt (${formatNumber(result.bannersFilledWebinar, 0)} Webinar)`
                    : `${formatNumber(result.bannersFilled, 0)} Banner jetzt ergänzt`
                }
              />
              {result.bannersRevoked > 0 && (
                <Chip
                  color="warning"
                  label={`${formatNumber(result.bannersRevoked, 0)} frühere Ergänzungen ${
                    result.dryRun ? 'würden zurückgenommen' : 'zurückgenommen'
                  }`}
                />
              )}
              {result.stubsFilled !== undefined && (
                <Chip
                  color="primary"
                  label={`${formatNumber(result.stubsFilled, 0)} unvollständige Bestellungen ${
                    result.dryRun ? 'würden' : 'werden'
                  } vorläufig ergänzt`}
                />
              )}
              {(result.stubsRevoked ?? 0) > 0 && (
                <Chip
                  color="warning"
                  label={`${formatNumber(result.stubsRevoked ?? 0, 0)} vorläufige Ergänzungen ${
                    result.dryRun ? 'würden zurückgenommen' : 'zurückgenommen'
                  }`}
                />
              )}
            </Box>
            {result.stubSamples && result.stubSamples.length > 0 && (
              <>
                <Typography variant="h6">
                  Unvollständige Bestellungen, die Produkt + WKZ aus PX-Data bekommen
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                  PL-X hat sie ohne Produkt, WKZ und Kunde geliefert. Sie zählen danach als Anfo, aber
                  nicht in Anfo-Wert und Conversion-Raten. Liefert PL-X eigene Werte, gelten diese.
                  {result.stubsSkipped &&
                    ` Nicht ergänzt: ${Object.entries(result.stubsSkipped)
                      .filter(([, n]) => n > 0)
                      .map(([k, n]) => `${STUB_SKIP_LABELS[k] ?? k} ${n}`)
                      .join(', ') || 'keine'}.`}
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bestellung</TableCell>
                      <TableCell>Datum</TableCell>
                      <TableCell>Produkt</TableCell>
                      <TableCell>WKZ laut PX-Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.stubSamples.map((s) => (
                      <TableRow key={s.ordernumber}>
                        <TableCell>{s.ordernumber}</TableCell>
                        <TableCell>{s.createdOn.slice(0, 16)}</TableCell>
                        <TableCell>{s.artikel}</TableCell>
                        <TableCell>{s.wkz}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
            <Typography variant="h6">Zahlart</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {Object.entries(result.paymentMethods)
                .sort((a, b) => b[1] - a[1])
                .map(([k, n]) => (
                  <Chip key={k} variant="outlined" label={`${k}: ${formatNumber(n, 0)}`} />
                ))}
            </Box>
            {result.filledSamples.length > 0 && (
              <>
                <Typography variant="h6">
                  Bestellungen, die einen Banner bekommen (neueste {result.filledSamples.length})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bestellung</TableCell>
                      <TableCell>Datum</TableCell>
                      <TableCell>Banner laut PX-Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.filledSamples.map((s) => (
                      <TableRow key={s.ordernumber}>
                        <TableCell>{s.ordernumber}</TableCell>
                        <TableCell>{s.createdOn.slice(0, 16)}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{s.banner}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {status?.tableReady && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Bisherige Importe
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {formatNumber(status.totals.rows, 0)} Bestellungen gespeichert, davon{' '}
              {formatNumber(status.totals.matchedSure, 0)} sicher zugeordnet ·{' '}
              {formatNumber(status.totals.bannersFilled, 0)} Banner ergänzt · letzte Bestellung{' '}
              {status.totals.maxOrderedAt ?? '–'}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Import</TableCell>
                  <TableCell>von</TableCell>
                  <TableCell align="right">Zeilen</TableCell>
                  <TableCell align="right">neu</TableCell>
                  <TableCell align="right">sicher</TableCell>
                  <TableCell align="right">unsicher</TableCell>
                  <TableCell align="right">ohne Treffer</TableCell>
                  <TableCell align="right">Banner ergänzt</TableCell>
                  <TableCell>letzte Bestellung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status.lastImports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.imported_at}</TableCell>
                    <TableCell>{r.imported_by ?? '–'}</TableCell>
                    <TableCell align="right">{formatNumber(Number(r.rows_total), 0)}</TableCell>
                    <TableCell align="right">{formatNumber(Number(r.rows_new), 0)}</TableCell>
                    <TableCell align="right">{formatNumber(Number(r.matched_sure), 0)}</TableCell>
                    <TableCell align="right">{formatNumber(Number(r.matched_unsure), 0)}</TableCell>
                    <TableCell align="right">{formatNumber(Number(r.unmatched), 0)}</TableCell>
                    <TableCell align="right">{formatNumber(Number(r.banners_filled), 0)}</TableCell>
                    <TableCell>{r.max_ordered_at ?? '–'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PxImportPage;
